import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Select,
  Skeleton,
  Text,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import {
  FiShoppingBag,
  FiTrendingUp,
  FiUsers,
  FiTruck,
  FiRefreshCw,
  FiCalendar,
  FiClock,
  FiStar,
  FiDollarSign,
  FiBarChart2,
  FiActivity,
} from "react-icons/fi";
import { baseUrl } from "../../utils/utils";

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, accentColor }) => (
  <Box
    bg="white"
    borderRadius="12px"
    boxShadow="0 1px 4px rgba(0,0,0,0.07)"
    borderLeft="4px solid"
    borderLeftColor={accentColor}
    p={5}
    display="flex"
    flexDirection="column"
    gap={3}
    transition="box-shadow 0.2s"
    _hover={{ boxShadow: "0 4px 16px rgba(0,0,0,0.10)" }}
  >
    <Flex justify="space-between" align="center">
      <Box
        w="40px"
        h="40px"
        borderRadius="10px"
        bg={`${accentColor.split(".")[0]}.50`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon as={icon} color={accentColor} boxSize={5} />
      </Box>
    </Flex>
    <Box>
      <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.8px" mb={1}>
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="800" color="gray.800" lineHeight="1.1">
        {value}
      </Text>
      {sub && (
        <Text fontSize="xs" color="gray.400" mt={1}>
          {sub}
        </Text>
      )}
    </Box>
  </Box>
);

// ─── Chart Card ────────────────────────────────────────────────────────────────
const ChartCard = ({ icon, title, children }) => (
  <Box
    bg="white"
    borderRadius="12px"
    boxShadow="0 1px 4px rgba(0,0,0,0.07)"
    p={6}
    // h="100%"
  >
    <Flex align="center" gap={3} mb={6}>
      <Box
        w="34px"
        h="34px"
        borderRadius="9px"
        bg="blue.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon as={icon} color="blue.500" boxSize={4} />
      </Box>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
    </Flex>
    {children}
  </Box>
);

// ─── Metric Tile ───────────────────────────────────────────────────────────────
const MetricTile = ({ label, value, color = "gray.700" }) => (
  <Box
    bg="gray.50"
    borderRadius="10px"
    p={5}
    textAlign="center"
    border="1.5px solid"
    borderColor="gray.100"
  >
    <Text fontSize="2xl" fontWeight="800" color={color} lineHeight="1.1" mb={1}>
      {value}
    </Text>
    <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.7px">
      {label}
    </Text>
  </Box>
);

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltipStyle = {
  backgroundColor: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  fontSize: "13px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
};

// ─── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodType, setPeriodType] = useState("Monthly");
  const [selectedMonth, setSelectedMonth] = useState("Dec 2024");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(baseUrl + "dashboard.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: "fd=2025-07-01&td=2025-12-31",
      });
      if (!response.ok) throw new Error("Failed to fetch dashboard data");
      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
      } else {
        throw new Error("API returned unsuccessful response");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateWeeklyData = () => {
    if (!dashboardData) return [];
    const avgOrders = Math.floor(dashboardData.total_orders / 4);
    const avgRevenue = Math.floor(dashboardData.total_revenue / 4 / 100000);
    return [
      { week: "Week 1", orders: avgOrders, revenue: avgRevenue },
      { week: "Week 2", orders: Math.floor(avgOrders * 0.85), revenue: Math.floor(avgRevenue * 0.9) },
      { week: "Week 3", orders: Math.floor(avgOrders * 1.1), revenue: Math.floor(avgRevenue * 1.05) },
      { week: "Week 4", orders: Math.floor(avgOrders * 1.05), revenue: Math.floor(avgRevenue * 1.1) },
    ];
  };

  const getDeliverySlotData = () => {
    if (!dashboardData) return [];
    return [
      { slot: "7–10 AM", count: Math.floor(dashboardData["10 AM to 1 PM"] * 0.6) || 0 },
      { slot: "10–1 PM", count: dashboardData["10 AM to 1 PM"] || 0 },
      { slot: "1–4 PM", count: dashboardData["1 PM to 4 PM"] || 0 },
      { slot: "4–7 PM", count: dashboardData["4 PM to 7 PM"] || 0},
    ];
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value}`;
  };

  const avgOrderValue = dashboardData ? Math.floor(dashboardData.total_revenue / dashboardData.total_orders) : 0;
  const activeCustomers = dashboardData ? dashboardData.total_orders - dashboardData.inactive_users : 0;
  const avgDeliveryTime = 28;
  const customerRating = 4.8;

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box p={6}>
        {/* Header skeleton */}
        <Flex justify="space-between" align="center" mb={8}>
          <Box>
            <Skeleton h="28px" w="160px" mb={2} borderRadius="8px" />
            <Skeleton h="14px" w="280px" borderRadius="8px" />
          </Box>
          <Flex gap={3}>
            <Skeleton h="38px" w="120px" borderRadius="8px" />
            <Skeleton h="38px" w="120px" borderRadius="8px" />
            <Skeleton h="38px" w="120px" borderRadius="8px" />
          </Flex>
        </Flex>
        {/* Stat cards skeleton */}
        <Grid templateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={5} mb={6}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} h="130px" borderRadius="12px" />
          ))}
        </Grid>
        {/* Charts skeleton */}
        <Grid templateColumns={["1fr", "1fr", "7fr 3fr"]} gap={5}>
          <Skeleton h="340px" borderRadius="12px" />
          <Skeleton h="340px" borderRadius="12px" />
        </Grid>
      </Box>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box p={6} display="flex" alignItems="center" justifyContent="center" minH="60vh">
        <Box bg="white" borderRadius="14px" boxShadow="0 2px 12px rgba(0,0,0,0.08)" p={10} maxW="400px" textAlign="center">
          <Box fontSize="40px" mb={4}>⚠️</Box>
          <Text fontWeight="700" fontSize="lg" color="gray.700" mb={2}>Failed to Load Data</Text>
          <Text fontSize="sm" color="gray.400" mb={6}>{error}</Text>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchDashboardData}
            bg="#0175c2"
            color="white"
            borderRadius="9px"
            size="md"
            _hover={{ bg: "#015a94" }}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  const BAR_COLOR = "#0175c2";

  return (
    <Box bg="#f7f8fa" minH="100vh" p={{ base: 4, md: 6 }}>

      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        flexWrap="wrap"
        gap={4}
        mb={6}
      >
        <Box>
          <Heading fontSize={{ base: "xl", md: "2xl" }} color="gray.800" fontWeight="800" mb={1}>
            Dashboard
          </Heading>
          <Text fontSize="sm" color="gray.400">
            Real-time overview of orders, revenue & delivery performance
          </Text>
        </Box>

        <Flex gap={3} flexWrap="wrap" align="flex-end">
          <Button
            leftIcon={<Icon as={FiCalendar} />}
            bg="#0175c2"
            color="white"
            size="sm"
            borderRadius="9px"
            px={4}
            h="36px"
            fontWeight="600"
            _hover={{ bg: "#015a94", transform: "translateY(-1px)" }}
            transition="all 0.2s"
            onClick={() => navigate("daily-roster")}
          >
            Daily Roster
          </Button>

          <Box>
            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb={1}>
              Period
            </Text>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              size="sm"
              borderRadius="9px"
              bg="white"
              border="1.5px solid"
              borderColor="gray.200"
              fontWeight="500"
              fontSize="sm"
              color="gray.700"
              h="36px"
              _focus={{ borderColor: "blue.300" }}
            >
              <option>Monthly</option>
              <option>Weekly</option>
              <option>Daily</option>
            </Select>
          </Box>

          <Box>
            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb={1}>
              Month
            </Text>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              size="sm"
              borderRadius="9px"
              bg="white"
              border="1.5px solid"
              borderColor="gray.200"
              fontWeight="500"
              fontSize="sm"
              color="gray.700"
              h="36px"
              _focus={{ borderColor: "blue.300" }}
            >
              <option>Dec 2024</option>
              <option>Nov 2024</option>
              <option>Oct 2024</option>
              <option>Sep 2024</option>
            </Select>
          </Box>
        </Flex>
      </Flex>

      {/* ── Stat Cards ──────────────────────────────────────────────────────── */}
      <Grid
        templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
        gap={4}
        mb={5}
      >
        <StatCard
          icon={FiShoppingBag}
          label="Total Orders"
          value={dashboardData?.total_orders?.toLocaleString() || "0"}
          sub="This period"
          accentColor="blue.500"
        />
        <StatCard
          icon={FiTrendingUp}
          label="Revenue"
          value={formatCurrency(dashboardData?.total_revenue || 0)}
          sub="This period"
          accentColor="green.500"
        />
        <StatCard
          icon={FiUsers}
          label="Active Customers"
          value={activeCustomers.toLocaleString()}
          sub="This period"
          accentColor="purple.500"
        />
        <StatCard
          icon={FiTruck}
          label="Delivery Rate"
          value={`${dashboardData?.delivery_rate?.toFixed(1) || 0}%`}
          sub="On-time deliveries"
          accentColor="orange.400"
        />
      </Grid>

      {/* ── Charts Row ──────────────────────────────────────────────────────── */}
      <Grid
        templateColumns={{ base: "1fr", lg: "1fr 320px" }}
        gap={4}
        mb={4}
      >
        {/* Revenue & Orders Trend */}
        <ChartCard icon={FiActivity} title="Revenue & Orders Trend">
          {/* Legend */}
          <HStack spacing={5} mb={5}>
            <Flex align="center" gap={2}>
              <Box w="10px" h="10px" borderRadius="50%" bg="#0175c2" />
              <Text fontSize="xs" color="gray.500" fontWeight="500">Orders</Text>
            </Flex>
            <Flex align="center" gap={2}>
              <Box w="10px" h="10px" borderRadius="50%" bg="#38a169" />
              <Text fontSize="xs" color="gray.500" fontWeight="500">Revenue (₹L)</Text>
            </Flex>
          </HStack>
          <ResponsiveContainer width="100%" height={270}>
            <LineChart data={generateWeeklyData()} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="week" stroke="#d1d5db" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis stroke="#d1d5db" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={CustomTooltipStyle} cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }} />
              <Line type="monotone" dataKey="orders" stroke="#0175c2" strokeWidth={2.5} dot={{ fill: "#0175c2", r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="revenue" stroke="#38a169" strokeWidth={2.5} dot={{ fill: "#38a169", r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Delivery Metrics */}
        <ChartCard icon={FiTruck} title="Delivery Metrics">
          <VStack spacing={3} align="stretch">
            <MetricTile label="Avg Delivery Time" value={`${avgDeliveryTime}m`} color="green.600" />
            <MetricTile label="Customer Rating" value={`${customerRating} ★`} color="blue.600" />
            <MetricTile label="Avg Order Value" value={`₹${avgOrderValue}`} color="gray.700" />
          </VStack>
        </ChartCard>
      </Grid>

      {/* ── Delivery Slot Chart ─────────────────────────────────────────────── */}
      <ChartCard icon={FiBarChart2} title="Delivery Slot Performance">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={getDeliverySlotData()} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="slot" stroke="#d1d5db" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis stroke="#d1d5db" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={CustomTooltipStyle} cursor={{ fill: "#f3f4f6" }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={72}>
              {getDeliverySlotData().map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={index === 0 ? "#0175c2" : index === 1 ? "#3b82f6" : index === 2 ? "#60a5fa" : "#93c5fd"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

    </Box>
  );
};

export default Dashboard;
